/**
 * One figure against what it is for, and the list it sits in.
 *
 * <p>
 * <strong>This existed four times.</strong> The booking detail, the issue counter, the return
 * counter and the new-booking summary each declared a private `Line` or `Row` — the same flex row
 * with a muted label and a right-aligned monospaced figure — and the four had drifted into four
 * vocabularies for the same three ideas. `held`, `brand` and no tone at all meant the money the shop
 * is holding; `due` and `warning` both meant money owed; one of the four wrote `strong` where the
 * others wrote `total`; and only one could carry a sentence under the figure. A customer reading a
 * quote and then a receipt was reading two designs.
 * </p>
 *
 * <p>
 * The tones are named for <strong>what the money is</strong> rather than for a colour, because that
 * is the part a caller has to get right: `brand` is money the shop holds against the garment,
 * `warning` is money still owed, `danger` and `success` are the two directions a settlement can
 * come out. Nothing else may take `warning` — amber is needs-attention everywhere in this product.
 * </p>
 *
 * <p>
 * <strong>One emphasised size, not two.</strong> The four copies disagreed on it (`text-base`
 * against `text-[15px]`), which is the shape of drift a shared component exists to end.
 * </p>
 */
export type MoneyTone = 'brand' | 'warning' | 'danger' | 'success';

const TONES: Record<MoneyTone, string> = {
  brand: 'text-primary',
  warning: 'text-warning',
  danger: 'text-danger',
  success: 'text-success',
};

/**
 * The list a money line belongs in.
 *
 * <p>
 * A `<dt>`/`<dd>` pair is only a description list inside a `<dl>`, so the wrapper is exported beside
 * the row rather than left to each caller to remember — one of the four call sites had it as
 * `<p><span><span>` and read to a screen reader as a run-on sentence.
 * </p>
 */
export function MoneyLines({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <dl className={`text-[13px] ${className}`}>{children}</dl>;
}

export function MoneyLine({
  label,
  hint,
  value,
  strong = false,
  total = false,
  tone,
  note,
}: {
  label: string;
  /** A quieter word beside the label — "already held", "at 1.5×". */
  hint?: string | undefined;
  value: string;
  /** The figure carries the weight, with no rule above it. What "payable" is on a running summary. */
  strong?: boolean;
  /** The summed line: ruled off above, both halves heavier. Implies {@link strong}. */
  total?: boolean;
  tone?: MoneyTone | undefined;
  /** A sentence under the figure, for a line somebody has to justify. */
  note?: string | null | undefined;
}) {
  const heavy = strong || total;

  return (
    <div
      className={`flex flex-wrap items-baseline justify-between gap-x-3 py-1.5 ${
        total ? 'border-border-color mt-1 border-t pt-2' : ''
      }`}
    >
      <dt className={total ? 'font-semibold text-gray-900' : 'text-default'}>
        {label}
        {hint && <span className="text-default ms-1 text-[11px]">{hint}</span>}
      </dt>
      <dd
        className={`font-mono-nums m-0 ${heavy ? 'text-base font-semibold' : 'font-medium'} ${
          tone ? TONES[tone] : 'text-gray-900'
        }`}
      >
        {value}
      </dd>
      {note ? <p className="text-default m-0 w-full text-[11.5px]">{note}</p> : null}
    </div>
  );
}
